import React, { useMemo } from 'react';
import {
  useEFormDefinition,
  useEFormDefinitions,
  useEFormSubmissions,
  usePublishedEForms,
} from '../../e-forms/useData';
import { StatCounterGrid } from './shared';
import type { StatCounterItem } from './shared';

function parseDefinitionId(...candidates: unknown[]): number | null {
  for (const raw of candidates) {
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
    if (typeof raw === 'string' && raw.trim()) {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) return n;
    }
    if (raw && typeof raw === 'object' && 'definitionId' in raw) {
      const nested = parseDefinitionId((raw as { definitionId?: unknown }).definitionId);
      if (nested != null) return nested;
    }
  }
  return null;
}

const EformsCatalogCounters: React.FC<{
  embedded?: boolean;
  definitionId?: number | string;
  settings?: Record<string, unknown>;
}> = ({ embedded, definitionId: definitionIdProp, settings }) => {
  const definitionId = parseDefinitionId(definitionIdProp, settings);
  const scoped = definitionId != null;

  const { data: form, isLoading: formLoading } = useEFormDefinition(definitionId ?? 0);
  const { total: submissionsTotal, isLoading: submissionsLoading } = useEFormSubmissions(
    definitionId,
    undefined,
    1,
    1,
  );

  const { total: definitionsTotal, isLoading: definitionsLoading } = useEFormDefinitions(
    undefined,
    1,
    1,
  );
  const { total: publishedTotal, isLoading: publishedTotalLoading } = usePublishedEForms(
    undefined,
    1,
    1,
    undefined,
    !scoped,
  );
  const { data: publishedList, isLoading: publishedListLoading } = usePublishedEForms(
    undefined,
    1,
    100,
    undefined,
    !scoped,
  );

  const submissionsSum = useMemo(
    () => (publishedList ?? []).reduce((sum, def) => sum + (def._count?.submissions ?? 0), 0),
    [publishedList],
  );

  const isLoading = scoped
    ? formLoading || submissionsLoading
    : definitionsLoading || publishedTotalLoading || publishedListLoading;

  const publishedVersions = form?.versions?.filter((v) => v.status === 'PUBLISHED').length ?? 0;
  const draftVersions = form?.versions?.filter((v) => v.status === 'DRAFT').length ?? 0;

  const counters: StatCounterItem[] = scoped
    ? [
        {
          key: 'submissions',
          label: 'Submissions',
          value: submissionsTotal ?? form?._count?.submissions ?? 0,
          href: `/e-forms/${definitionId}/submissions`,
          accent: 'bg-emerald-500',
        },
        {
          key: 'published',
          label: 'Published versions',
          value: publishedVersions,
          href: `/e-forms/builder/${definitionId}?section=publish`,
          accent: 'bg-sky-500',
        },
        {
          key: 'draft',
          label: 'Draft versions',
          value: draftVersions,
          href: `/e-forms/builder/${definitionId}?section=design`,
          accent: 'bg-slate-500',
        },
      ]
    : [
        {
          key: 'definitions',
          label: 'Active definitions',
          value: definitionsTotal ?? 0,
          href: '/e-forms',
          accent: 'bg-slate-500',
        },
        {
          key: 'published',
          label: 'Published',
          value: publishedTotal ?? 0,
          href: '/e-forms',
          accent: 'bg-sky-500',
        },
        {
          key: 'submissions',
          label: 'Submissions (catalog)',
          value: submissionsSum,
          href: '/e-forms',
          accent: 'bg-emerald-500',
        },
      ];

  return (
    <StatCounterGrid
      title={scoped ? `E-Forms · ${form?.name || 'Form'}` : 'E-Forms · Catalog'}
      counters={counters}
      isLoading={isLoading}
      embedded={embedded}
      viewAllPath={scoped ? `/e-forms/${definitionId}/submissions` : '/e-forms'}
    />
  );
};

export default EformsCatalogCounters;
